import { type CSSProperties, type MouseEvent, useLayoutEffect, useRef } from "react";
import {
  amount,
  COUNT_DELAY_MS,
  COUNT_MS,
  type Dir,
  type DuelRound,
  driftDelay,
  easeOutCubic,
  type MetricKey,
  WORDS,
} from "./duel";
import { Tri } from "./icons";

export function reduceMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}

type Mode = "static" | "count" | "hidden";

interface DuelHalfProps {
  round: DuelRound;
  /** 0 = known (top), 1 = the one to judge, 2 = next in line (below the frame). */
  slot: number;
  metric: MetricKey;
  phase: "ask" | "reveal" | "slide";
  verdictOn: boolean;
  right: boolean;
  truth: string | null;
  chosen: Dir | null;
  busy: boolean;
  onPick: (dir: Dir) => void;
}

/**
 * One half of the stage. Every half has the same two layers (the number, the
 * question) stacked in one cell, so a half keeps its exact layout while it slides
 * from the bottom role into the top one.
 */
export function DuelHalf({ round, slot, metric, phase, verdictOn, right, truth, chosen, busy, onPick }: DuelHalfProps) {
  const words = WORDS[metric];
  const asking = slot === 2 || (slot === 1 && phase === "ask");
  const mode: Mode = slot === 0 || (slot === 1 && phase === "slide") ? "static" : slot === 1 && phase === "reveal" ? "count" : "hidden";
  const pillOn = slot === 1 && phase === "reveal" && verdictOn;
  const capOn = slot === 0 || (slot === 1 && phase === "slide");
  const live = slot === 1 && phase === "ask";

  const press = (dir: Dir) => (e: MouseEvent) => {
    e.stopPropagation();
    onPick(dir);
  };

  return (
    <section className="du-half" data-slot={slot}>
      <img
        className="du-art"
        src={`/flags/${round.code.toLowerCase()}.svg`}
        alt=""
        draggable={false}
        style={{ animationDelay: `-${driftDelay(round.code)}s` } as CSSProperties}
      />
      <div className="du-body">
        <div className="du-name">{round.name}</div>
        <div className="du-swap">
          <div className="du-layer" data-on={!asking} aria-hidden={asking}>
            <Amount metric={metric} value={round.val} mode={mode} />
            <div className="du-sub">
              {slot === 1 && truth && (
                <span className={`du-pill ${right ? "du-pill-ok" : "du-pill-bad"}`} data-on={pillOn}>
                  {right ? "так" : "ні"}, {truth}
                </span>
              )}
              <span className="du-cap" data-on={capOn}>
                {words.caption}
              </span>
            </div>
          </div>
          <div className="du-layer" data-on={asking} aria-hidden={!asking} inert={!asking}>
            <div className="du-ask">{words.ask}</div>
            <div className="du-btns" data-waiting={live && chosen !== null}>
              <button
                type="button"
                className="du-btn du-btn-more"
                data-chosen={live && chosen === "up"}
                disabled={!live || busy || chosen !== null}
                onClick={press("up")}
              >
                <Tri up /> Більше
              </button>
              <button
                type="button"
                className="du-btn du-btn-less"
                data-chosen={live && chosen === "down"}
                disabled={!live || busy || chosen !== null}
                onClick={press("down")}
              >
                <Tri up={false} /> Менше
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/** The big number. Its digits are written straight into the span so a count-up doesn't re-render the stage. */
function Amount({ metric, value, mode }: { metric: MetricKey; value: number | undefined; mode: Mode }) {
  const digits = useRef<HTMLSpanElement>(null);
  const final = value === undefined ? null : amount(metric, value);

  useLayoutEffect(() => {
    const el = digits.current;
    if (!el) return;
    if (value === undefined || mode === "hidden") {
      el.textContent = "";
      return;
    }
    if (mode === "static" || reduceMotion()) {
      el.textContent = amount(metric, value).num;
      return;
    }
    const from = performance.now() + COUNT_DELAY_MS;
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, Math.max(0, (now - from) / COUNT_MS));
      el.textContent = amount(metric, value, value * easeOutCubic(t)).num;
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    el.textContent = amount(metric, value, 0).num;
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      el.textContent = amount(metric, value).num;
    };
  }, [metric, value, mode]);

  return (
    <div className="du-num">
      {final?.prefix && mode !== "hidden" && <span className="du-prefix">{final.prefix}</span>}
      <span ref={digits} />
      {final && mode !== "hidden" && <span className="du-unit">{final.unit}</span>}
    </div>
  );
}
